import { yupResolver } from '@hookform/resolvers/yup';
import { Button, FormControlLabel, MenuItem, Radio } from '@mui/material';
import FormInput from 'app/shared-components/formComponents/FormInput';
import FormSelect from 'app/shared-components/formComponents/FormSelect';
import { FormProvider, useForm } from 'react-hook-form';

import FormRadioGroup from 'app/shared-components/formComponents/FormRadioGroup';
import statesList from 'src/app/consts/statesList';
import { useStore } from 'zustand';
import { IKurvBasicInformationFormValues, kurvBasicInformationSchema } from './validation';
import { useCurrentMerchant } from 'src/app/queries/merchant.queries';
import { kurvFormSlice } from 'app/store/kurvFormSlice';

interface IProps {
  index: number;
  setNextForm: () => void;
  setPrevForm: () => void;
}

const KurvBasicInformationForm = ({ setNextForm, setPrevForm, index }: IProps) => {
  const store = useStore(kurvFormSlice);
  const { data: currentMerchant } = useCurrentMerchant();
  const merchant = currentMerchant?.data;

  const methods = useForm<IKurvBasicInformationFormValues>({
    mode: 'onChange',
    values: store.basicInformation,
    defaultValues: {
      address: merchant?.address1,
      city: merchant?.city,
      state: merchant?.state,
      zip: merchant?.zipCode,
    },
    resolver: yupResolver(kurvBasicInformationSchema),
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = methods;

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col flex-1 justify-between mt-16"
        onSubmit={handleSubmit((d) => {
          store.setBasicInformation(d);
          setNextForm();
        })}
      >
        <div>
          <FormRadioGroup
            id="processing"
            control={control}
            label="Processing"
            labelClassName="mt-20"
            defaultValue={false}
          >
            <FormControlLabel control={<Radio />} label="Yes" value />
            <FormControlLabel control={<Radio />} label="No" value={false} />
          </FormRadioGroup>

          <FormInput
            className="mt-20"
            id="ownership"
            label="Ownership %*"
            errorMessage={errors.ownership?.message}
            control={control}
            variant="outlined"
            fullWidth
          />
          <FormInput
            className="mt-20"
            id="address"
            label="Address*"
            errorMessage={errors.address?.message}
            control={control}
            variant="outlined"
            fullWidth
          />
          <FormInput
            className="mt-20"
            id="city"
            label="City*"
            errorMessage={errors.city?.message}
            control={control}
            variant="outlined"
            fullWidth
          />
          <FormSelect
            id="state"
            label="State*"
            control={control}
            error={errors.state?.message}
            defaultValue=""
            sx={{ minWidth: 120 }}
            className="mt-20"
            fullWidth
          >
            {statesList.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </FormSelect>
          <FormInput
            className="mt-20"
            id="county"
            label="County*"
            errorMessage={errors.county?.message}
            control={control}
            variant="outlined"
            fullWidth
          />
          <FormInput
            className="mt-20"
            id="zip"
            label="Zip code*"
            errorMessage={errors.zip?.message}
            control={control}
            variant="outlined"
            fullWidth
          />
        </div>

        <div className="flex justify-end gap-[10px]">
          <Button
            variant="text"
            color="secondary"
            className="mt-16 text-[#111827] disabled:text-dark-primary disabled:text-opacity-60"
            aria-label="Back"
            type="button"
            onClick={setPrevForm}
            size="large"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            className="mt-16 text-white bg-blue-primary disabled:text-dark-primary disabled:bg-gray-400 disabled:text-opacity-60"
            aria-label="Next step"
            type="submit"
            size="large"
            disabled={!isValid}
          >
            Next step
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default KurvBasicInformationForm;
